import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";

const BookingLookup = () => {
    const location = useLocation();
    const isEnglish = location.pathname.startsWith("/en");

    const [bookId, setBookId] = useState("");
    const [email, setEmail] = useState("");
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const [message, setMessage] = useState("");
    const [error, setError] = useState("");

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    // 日英のベーステキスト
    const t = {
        title: isEnglish ? "Manage or Cancel Booking" : "ご予約の確認・キャンセル",
        subtitle: isEnglish
            ? "Please enter your Booking ID and registered email address to view or cancel your reservation."
            : "ご予約内容の確認、またはキャンセルの手続きが行えます。",
        labelBookId: isEnglish ? "Booking ID" : "予約番号 (Booking ID)",
        placeholderBookId: "ex: 92562405",
        labelEmail: isEnglish ? "Registered Email" : "ご登録メールアドレス",
        placeholderEmail: "example@email.com",
        btnSearch: isEnglish ? "Search Booking" : "予約内容を検索する",
        btnSearching: isEnglish ? "Searching..." : "検索中...",
        errNotFound: isEnglish ? "Booking not found." : "予約が見つかりませんでした。",
        errNetwork: isEnglish ? "A network error occurred." : "通信エラーが発生しました。",
        btnCancel: isEnglish ? "Cancel this Booking" : "この予約をキャンセルする",
        btnCancelling: isEnglish ? "Processing..." : "キャンセル処理中...",
        errCancelFailed: isEnglish ? "Cancellation failed." : "キャンセル処理に失敗しました。",
        bookingDetails: isEnglish ? "Booking Details" : "ご予約内容",
        bName: isEnglish ? "Guest Name" : "お名前",
        bCheckIn: isEnglish ? "Check-in" : "チェックイン",
        bCheckOut: isEnglish ? "Check-out" : "チェックアウト",
        bGuests: isEnglish ? "Guests" : "ご宿泊人数",
        bTotal: isEnglish ? "Total Price" : "合計料金",
        bStatus: isEnglish ? "Status" : "ステータス",
        statusCancelled: isEnglish ? "Cancelled" : "キャンセル済み",
        statusConfirmed: isEnglish ? "Confirmed" : "ご予約確定",
    };

    // ▼ 返金可能かどうかの判定 (JST基準) ▼
    let isRefundable = false;
    if (booking && booking.status !== "0") {
        const checkInDate = new Date(booking.checkIn);
        checkInDate.setHours(0, 0, 0, 0);

        const now = new Date();
        const jstDate = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Tokyo' }));
        jstDate.setHours(0, 0, 0, 0);

        const diffDays = Math.ceil((checkInDate.getTime() - jstDate.getTime()) / (1000 * 60 * 60 * 24));
        isRefundable = diffDays > 14;
    }

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setMessage("");
        setBooking(null);

        try {
            const res = await fetch("/api/getBooking", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ bookId: bookId.trim(), email: email.trim() }),
            });
            const data = await res.json();

            if (data.success) {
                setBooking(data.booking);
            } else {
                setError(data.error || t.errNotFound);
            }
        } catch (err) {
            setError(t.errNetwork);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async () => {
        // ▼ 返金可能かどうかに応じてポップアップの警告文を切り替え ▼
        let confirmMessage = "";
        if (isRefundable) {
            confirmMessage = isEnglish
                ? "Are you sure you want to cancel this booking?\nYour payment will be fully refunded."
                : "本当にこのご予約をキャンセルしますか？\n決済済みの代金は全額返金されます。";
        } else {
            confirmMessage = isEnglish
                ? "Are you sure you want to cancel this booking?\n\n* WARNING: You are within 14 days of your arrival date. A 100% cancellation fee applies and NO REFUND will be issued."
                : "本当にこのご予約をキャンセルしますか？\n\n※【警告】到着日の14日前を過ぎているため、キャンセル料が100%発生します。ご返金はございません。";
        }

        if (!window.confirm(confirmMessage)) {
            return;
        }

        setCancelling(true);
        setError("");

        try {
            const res = await fetch("/api/cancelBooking", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ bookId: booking.bookId, email: booking.guestEmail }),
            });
            const data = await res.json();

            if (data.success) {
                setMessage(data.message);
                setBooking({ ...booking, status: "0" });
            } else {
                setError(data.error || t.errCancelFailed);
            }
        } catch (err) {
            setError(t.errNetwork);
        } finally {
            setCancelling(false);
        }
    };

    return (
        <div style={{
            paddingTop: '150px',
            paddingBottom: '100px',
            backgroundColor: '#fdfbf7',
            minHeight: '100vh',
            width: '100%'
        }}>
            <div style={{
                maxWidth: "650px",
                margin: "0 auto",
                padding: "40px 30px",
                backgroundColor: "#fff",
                border: "1px solid #e0d8c0",
                borderRadius: "8px",
                boxShadow: "0 10px 30px rgba(8, 7, 82, 0.05)"
            }}>
                <div style={{ textAlign: "center", marginBottom: "35px" }}>
                    <h2 style={{
                        color: "#080752",
                        fontFamily: '"Noto Serif JP", serif',
                        fontSize: "1.8rem",
                        marginBottom: "15px",
                        letterSpacing: "0.05em"
                    }}>
                        {t.title}
                    </h2>
                    <p style={{ color: "#666", fontSize: "0.95rem", lineHeight: "1.6" }}>
                        {t.subtitle}
                    </p>
                </div>

                <form onSubmit={handleSearch} style={{ display: "flex", flexDirection: "column", gap: "25px" }}>
                    <div>
                        <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold", color: "#333", fontSize: "0.95rem" }}>
                            {t.labelBookId}
                        </label>
                        <input
                            type="text"
                            required
                            placeholder={t.placeholderBookId}
                            value={bookId}
                            onChange={(e) => setBookId(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "14px 15px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "1rem",
                                backgroundColor: "#f9f9f9",
                                transition: "border-color 0.3s"
                            }}
                            onFocus={(e) => e.target.style.borderColor = "#080752"}
                            onBlur={(e) => e.target.style.borderColor = "#ddd"}
                        />
                    </div>

                    <div>
                        <label style={{ display: "block", marginBottom: "8px", fontWeight: "bold", color: "#333", fontSize: "0.95rem" }}>
                            {t.labelEmail}
                        </label>
                        <input
                            type="email"
                            required
                            placeholder={t.placeholderEmail}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            style={{
                                width: "100%",
                                padding: "14px 15px",
                                border: "1px solid #ddd",
                                borderRadius: "4px",
                                fontSize: "1rem",
                                backgroundColor: "#f9f9f9",
                                transition: "border-color 0.3s"
                            }}
                            onFocus={(e) => e.target.style.borderColor = "#080752"}
                            onBlur={(e) => e.target.style.borderColor = "#ddd"}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        style={{
                            marginTop: "10px",
                            padding: "16px",
                            backgroundColor: "#080752",
                            color: "#fff",
                            border: "none",
                            borderRadius: "4px",
                            fontSize: "1.05rem",
                            fontWeight: "bold",
                            letterSpacing: "0.05em",
                            cursor: loading ? "not-allowed" : "pointer",
                            transition: "background-color 0.3s",
                            opacity: loading ? 0.8 : 1
                        }}
                        onMouseOver={(e) => !loading && (e.target.style.backgroundColor = "#1a1873")}
                        onMouseOut={(e) => !loading && (e.target.style.backgroundColor = "#080752")}
                    >
                        {loading ? t.btnSearching : t.btnSearch}
                    </button>
                </form>

                {error && (
                    <div style={{ marginTop: "25px", padding: "15px", backgroundColor: "#fdf0f0", borderLeft: "4px solid #d9534f", color: "#d9534f", borderRadius: "4px" }}>
                        {error}
                    </div>
                )}

                {message && (
                    <div style={{ marginTop: "25px", padding: "15px", backgroundColor: "#f0fdf4", borderLeft: "4px solid #2E7D32", color: "#2E7D32", borderRadius: "4px", fontWeight: "bold" }}>
                        {message}
                    </div>
                )}

                {booking && (
                    <div style={{ marginTop: "40px", paddingTop: "30px", borderTop: "2px dashed #e0d8c0" }}>
                        <h3 style={{ color: "#080752", marginBottom: "25px", fontSize: "1.3rem", fontFamily: '"Noto Serif JP", serif', textAlign: "center" }}>
                            {t.bookingDetails}
                        </h3>

                        <div style={{ display: "flex", flexDirection: "column", gap: "15px", backgroundColor: "#fdfbf7", padding: "20px", borderRadius: "6px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
                                <span style={{ color: "#666" }}>{t.labelBookId}</span>
                                <strong style={{ color: "#333" }}>{booking.bookId}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
                                <span style={{ color: "#666" }}>{t.bName}</span>
                                <strong style={{ color: "#333" }}>{booking.guestName}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
                                <span style={{ color: "#666" }}>{t.bCheckIn}</span>
                                <strong style={{ color: "#333" }}>{booking.checkIn}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
                                <span style={{ color: "#666" }}>{t.bCheckOut}</span>
                                <strong style={{ color: "#333" }}>{booking.checkOut}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
                                <span style={{ color: "#666" }}>{t.bGuests}</span>
                                <strong style={{ color: "#333" }}>{booking.numAdult}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "1px solid #eee", paddingBottom: "10px" }}>
                                <span style={{ color: "#666" }}>{t.bTotal}</span>
                                <strong style={{ color: "#b76e79", fontSize: "1.1rem" }}>¥{Number(booking.price).toLocaleString()}</strong>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", paddingTop: "5px" }}>
                                <span style={{ color: "#666" }}>{t.bStatus}</span>
                                {booking.status === "0" ? (
                                    <span style={{ color: "#d9534f", fontWeight: "bold", padding: "4px 8px", backgroundColor: "#fdf0f0", borderRadius: "4px" }}>{t.statusCancelled}</span>
                                ) : (
                                    <span style={{ color: "#2E7D32", fontWeight: "bold", padding: "4px 8px", backgroundColor: "#f0fdf4", borderRadius: "4px" }}>{t.statusConfirmed}</span>
                                )}
                            </div>
                        </div>

                        {booking.status !== "0" && (
                            <div style={{ marginTop: "30px", textAlign: "center" }}>
                                <button
                                    onClick={handleCancel}
                                    disabled={cancelling}
                                    style={{
                                        width: "100%",
                                        padding: "16px",
                                        backgroundColor: "transparent",
                                        color: "#d9534f",
                                        border: "1px solid #d9534f",
                                        borderRadius: "4px",
                                        fontSize: "1rem",
                                        fontWeight: "bold",
                                        cursor: cancelling ? "not-allowed" : "pointer",
                                        transition: "all 0.3s ease",
                                        opacity: cancelling ? 0.7 : 1
                                    }}
                                    onMouseOver={(e) => !cancelling && (e.target.style.backgroundColor = "#fdf0f0")}
                                    onMouseOut={(e) => !cancelling && (e.target.style.backgroundColor = "transparent")}
                                >
                                    {cancelling ? t.btnCancelling : t.btnCancel}
                                </button>

                                {/* ▼ 返金可能かどうかに応じてボタン下の案内テキストも切り替え ▼ */}
                                <p style={{ fontSize: "0.85rem", color: isRefundable ? "#2E7D32" : "#d9534f", marginTop: "12px", lineHeight: "1.5", fontWeight: isRefundable ? "normal" : "bold" }}>
                                    {isRefundable
                                        ? (isEnglish ? "You are currently outside the 14-day cancellation window. You will receive a full refund." : "現在、無料キャンセル期間内のため、全額が返金されます。")
                                        : (isEnglish ? "* As this is within 14 days of your check-in date, a 100% cancellation fee applies and this is non-refundable." : "※到着日の14日前を過ぎているため、キャンセル料が100%発生いたします。")}
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BookingLookup;